import React from 'react';
import { PageSection, SiteData, CorporateServiceItem } from '../../../types';
import {
  Compass,
  TrendingUp,
  Cpu,
  ShieldCheck,
  Zap,
  Award,
  BarChart3,
  CheckCircle2,
  Plus,
  ArrowRight,
  Sparkles,
  Building,
  Briefcase,
  Layers,
} from 'lucide-react';
import { WebsiteButton } from '../../common/WebsiteButton';
import { isDarkBackground, getCardStylesForBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
  onAddService?: () => void;
  isPreview?: boolean;
}

export const ServicesSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
  onAddService,
  isPreview = false,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'UZMANLIK ALANLARI';
  const title = content.title || 'HİZMETLERİMİZ';
  const description =
    content.description ||
    'Sektörün ihtiyaç duyduğu modern stratejik, operasyonel ve teknolojik çözümleri tek çatı altında sunuyoruz.';

  // Read centralized DB services if available
  const rawItems =
    content.items && content.items.length > 0
      ? content.items
      : siteData.settings?.servicesList && siteData.settings.servicesList.length > 0
      ? siteData.settings.servicesList
      : [];

  const items: CorporateServiceItem[] = rawItems.map((item: any, idx: number) => ({
    id: item.id || `serv-${idx}`,
    title: item.title || 'Kurumsal Hizmet',
    slug: item.slug || `hizmet-${idx + 1}`,
    shortDescription: item.shortDescription || item.description || 'Profesyonel kurumsal hizmet çözümü.',
    description: item.description || item.shortDescription || '',
    icon: item.icon || 'Briefcase',
    coverImage: item.coverImage || item.imageUrl,
    price: item.price,
    order: item.order ?? idx,
  }));

  const bgColor = style.backgroundColor || '#F8FAFC';
  const isDark = isDarkBackground(bgColor);
  const cardStyles = getCardStylesForBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const columnsCount = Number(content.columns) || 3;
  const gridColsClass =
    columnsCount === 2
      ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
      : columnsCount === 4
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  const getIcon = (iconName?: string) => {
    switch (iconName) {
      case 'TrendingUp':
        return <TrendingUp className="w-5 h-5" />;
      case 'Cpu':
        return <Cpu className="w-5 h-5" />;
      case 'ShieldCheck':
        return <ShieldCheck className="w-5 h-5" />;
      case 'Zap':
        return <Zap className="w-5 h-5" />;
      case 'Award':
        return <Award className="w-5 h-5" />;
      case 'BarChart3':
        return <BarChart3 className="w-5 h-5" />;
      case 'Building':
        return <Building className="w-5 h-5" />;
      case 'Layers':
        return <Layers className="w-5 h-5" />;
      default:
        return <Briefcase className="w-5 h-5" />;
    }
  };

  const anchorId = style.anchorId || style.customId || 'services';

  return (
    <section
      id={anchorId}
      data-section-id={section.id}
      data-section-type="SERVICES"
      className="py-20 px-4 sm:px-6 lg:px-8 select-none transition-colors"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          {badge && (
            <div
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-black tracking-widest uppercase border"
              style={{
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}10`,
                color: accentColor,
              }}
            >
              <Sparkles className="w-3 h-3" />
              <span>{badge}</span>
            </div>
          )}

          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {title}
          </h2>

          <p className={`text-base sm:text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {description}
          </p>
        </div>

        {/* Empty State */}
        {items.length === 0 ? (
          <div className="p-12 text-center rounded-3xl border-2 border-dashed border-slate-300 dark:border-slate-700 max-w-lg mx-auto bg-slate-50 dark:bg-slate-900/50 space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-500/10 text-blue-500 mx-auto flex items-center justify-center">
              <Briefcase className="w-7 h-7" />
            </div>
            <div className="space-y-1">
              <h3 className="font-bold text-base text-slate-800 dark:text-slate-200">Henüz Hizmet Eklenmedi</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Sol panelden veya aşağıdaki butonu kullanarak işletmenize ait ilk hizmeti ekleyin.
              </p>
            </div>
            {!isPreview && onAddService && (
              <button
                type="button"
                onClick={onAddService}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:scale-105 transition"
                style={{ backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }}
              >
                <Plus className="w-4 h-4" />
                <span>+ Hizmet Ekle</span>
              </button>
            )}
          </div>
        ) : (
          /* Dynamic Services Grid with Auto-Contrast */
          <div className={`grid ${gridColsClass} gap-6`}>
            {items.map((srv, idx) => (
              <div
                key={srv.id || idx}
                className={`group rounded-3xl p-6 border transition-all duration-300 flex flex-col justify-between shadow-xs hover:shadow-xl hover:-translate-y-1 ${cardStyles.bg} ${cardStyles.border}`}
              >
                <div className="space-y-4">
                  {/* Optional Cover Image */}
                  {srv.coverImage && (
                    <div className="rounded-2xl overflow-hidden aspect-video bg-slate-100 dark:bg-slate-800 -mx-2 -mt-2 mb-2">
                      <img
                        src={srv.coverImage}
                        alt={srv.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                      />
                    </div>
                  )}

                  {/* Icon & Badge */}
                  <div className="flex items-center justify-between">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md transition-transform group-hover:scale-110"
                      style={{
                        backgroundColor: `${accentColor}15`,
                        color: accentColor,
                      }}
                    >
                      {getIcon(srv.icon)}
                    </div>
                    {srv.price && (
                      <span className={`text-[11px] font-extrabold px-2.5 py-1 rounded-full ${isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}`}>
                        {srv.price}
                      </span>
                    )}
                  </div>

                  {/* Title & Short Description */}
                  <div className="space-y-2">
                    <h3 className={`text-lg font-black tracking-tight leading-snug ${cardStyles.heading}`}>
                      {srv.title}
                    </h3>
                    <p className={`text-xs leading-relaxed line-clamp-3 ${cardStyles.mutedText}`}>
                      {srv.shortDescription || (srv as any).description || 'Kurumsal standartlarda profesyonel çözüm ve operasyonel destek.'}
                    </p>
                  </div>
                </div>

                {/* Card Action Button */}
                <div className="pt-6 mt-4 border-t border-slate-200/40 dark:border-slate-800/80">
                  <WebsiteButton
                    label="Detayları İncele"
                    actionType="service"
                    serviceSlug={srv.slug || srv.id}
                    variant="ghost"
                    size="sm"
                    className={`w-full justify-between !px-0 !py-1 ${cardStyles.heading} hover:text-blue-500 group-hover:translate-x-1`}
                    onNavigate={onNavigate}
                    isEditor={false}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* In Editor mode: Add Service Button */}
        {!isPreview && onAddService && items.length > 0 && (
          <div className="text-center pt-4">
            <button
              type="button"
              onClick={onAddService}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border border-dashed border-blue-500/50 bg-blue-500/5 hover:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold text-xs transition active:scale-95"
            >
              <Plus className="w-4 h-4" />
              <span>+ Yeni Hizmet Ekle</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
